import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Redirect, useHistory } from "react-router-dom";
import { Nav, Tab, Table, Form, Button, Badge, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import CommonPageHeader from "../../../common/ui/page-header/CommonPageHeader";
import { clearSelectedComp } from "../../../Global/CustomFunctions";
import { Common } from "../../../../utils";
import { enterSuperAdminCompany, isSuperAdminRole } from "../../../../utils/superAdminContext";
import * as officeActions from "../companyOffices/officeActions";
import * as companyActions from "../companyActions";
import {
  getSuperAdminCompanies,
  getSuperAdminDashboard,
  getSuperAdminPayments,
  getSuperAdminUsers,
} from "./superAdminActions";
import SuperAdminTicketsPanel from "./SuperAdminTicketsPanel";
import "./superAdminDashboard.scss";

const pick = (obj, ...keys) => {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

const normalizeDashboard = (d) => ({
  totalCompanies: pick(d, "totalCompanies", "TotalCompanies") ?? 0,
  totalUsers: pick(d, "totalUsers", "TotalUsers") ?? 0,
  activeUsers: pick(d, "activeUsers", "ActiveUsers") ?? 0,
  companiesRegisteredLast30Days: pick(d, "companiesRegisteredLast30Days", "CompaniesRegisteredLast30Days") ?? 0,
  usersRegisteredLast30Days: pick(d, "usersRegisteredLast30Days", "UsersRegisteredLast30Days") ?? 0,
  loginsLast30Days: pick(d, "loginsLast30Days", "LoginsLast30Days") ?? 0,
  activeSubscriptions: pick(d, "activeSubscriptions", "ActiveSubscriptions") ?? 0,
  trialSubscriptions: pick(d, "trialSubscriptions", "TrialSubscriptions") ?? 0,
  suspendedSubscriptions: pick(d, "suspendedSubscriptions", "SuspendedSubscriptions") ?? 0,
});

const normalizeCompany = (c) => ({
  id: pick(c, "id", "Id"),
  name: pick(c, "name", "Name"),
  companyEmail: pick(c, "companyEmail", "CompanyEmail"),
  createdDate: pick(c, "createdDate", "CreatedDate"),
  userCount: pick(c, "userCount", "UserCount") ?? 0,
  activeUserCount: pick(c, "activeUserCount", "ActiveUserCount") ?? 0,
  subscriptionStatus: pick(c, "subscriptionStatus", "SubscriptionStatus"),
  lastLoginUtc: pick(c, "lastLoginUtc", "LastLoginUtc"),
});

const normalizeUser = (u) => ({
  id: pick(u, "id", "Id"),
  name: pick(u, "name", "Name"),
  email: pick(u, "email", "Email"),
  companyId: pick(u, "companyId", "CompanyId"),
  companyName: pick(u, "companyName", "CompanyName"),
  isAccountActive: pick(u, "isAccountActive", "IsAccountActive"),
  isCompanyAdmin: pick(u, "isCompanyAdmin", "IsCompanyAdmin"),
  accountCreationDate: pick(u, "accountCreationDate", "AccountCreationDate"),
  lastLoginUtc: pick(u, "lastLoginUtc", "LastLoginUtc"),
  mainRole: pick(u, "mainRole", "MainRole"),
});

const normalizePayment = (p) => ({
  id: pick(p, "id", "Id"),
  companyId: pick(p, "companyId", "CompanyId"),
  companyName: pick(p, "companyName", "CompanyName"),
  amount: pick(p, "amount", "Amount"),
  currency: pick(p, "currency", "Currency"),
  paymentType: pick(p, "paymentType", "PaymentType"),
  paymentStatus: pick(p, "paymentStatus", "PaymentStatus"),
  paymentDate: pick(p, "paymentDate", "PaymentDate"),
  createdDate: pick(p, "createdDate", "CreatedDate"),
  paymentReference: pick(p, "paymentReference", "PaymentReference"),
  userCount: pick(p, "userCount", "UserCount"),
  customerEmail: pick(p, "customerEmail", "CustomerEmail"),
});

const formatDate = (value, { withTime = false } = {}) => {
  if (!value) return "—";
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    const options = withTime
      ? {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }
      : { month: "short", day: "numeric", year: "numeric" };
    return date.toLocaleDateString("en-US", options);
  } catch (_) {
    return "—";
  }
};

const formatMoney = (amount, currency) => {
  if (amount == null) return "—";
  const cur = currency || "USD";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: cur }).format(amount);
  } catch (_) {
    return `${amount} ${cur}`;
  }
};

const StatCard = ({ icon, label, value, hint }) => (
  <div className="sa-stat-card">
    <div className="sa-stat-icon">
      <i className={`bi ${icon}`} />
    </div>
    <div className="sa-stat-body">
      <div className="sa-stat-value">{value ?? "—"}</div>
      <div className="sa-stat-label">{label}</div>
      {hint ? <div className="sa-stat-hint">{hint}</div> : null}
    </div>
  </div>
);

const compareSortValues = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") return Number(a) - Number(b);
  const aTime = a instanceof Date || (typeof a === "string" && !Number.isNaN(Date.parse(a)) && /\d{4}-\d{2}/.test(String(a)))
    ? Date.parse(a)
    : NaN;
  const bTime = b instanceof Date || (typeof b === "string" && !Number.isNaN(Date.parse(b)) && /\d{4}-\d{2}/.test(String(b)))
    ? Date.parse(b)
    : NaN;
  if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) return aTime - bTime;
  return String(a).localeCompare(String(b), undefined, { sensitivity: "base", numeric: true });
};

const sortRows = (rows, sort) => {
  if (!sort?.key || !Array.isArray(rows)) return rows || [];
  const dir = sort.dir === "asc" ? 1 : -1;
  return [...rows].sort((left, right) => {
    const result = compareSortValues(left?.[sort.key], right?.[sort.key]);
    return result * dir;
  });
};

const SortableTh = ({ label, sortKey, sort, onSort, className = "" }) => {
  const active = sort?.key === sortKey;
  const icon = !active ? "bi-arrow-down-up" : sort.dir === "asc" ? "bi-sort-up" : "bi-sort-down";
  return (
    <th className={`sa-sortable-th ${active ? "is-active" : ""} ${className}`.trim()}>
      <button
        type="button"
        className="sa-sort-btn"
        onClick={() => onSort(sortKey)}
        aria-label={`Sort by ${label}`}
      >
        <span>{label}</span>
        <i className={`bi ${icon}`} aria-hidden="true" />
      </button>
    </th>
  );
};

const toggleSort = (prev, key, defaultDir = "asc") => {
  if (prev?.key === key) {
    return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
  }
  return { key, dir: defaultDir };
};

const SuperAdminDashboard = ({ companyActions: companyActs, officeActions: officeActs }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [activeTab, setActiveTab] = useState("companies");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [payments, setPayments] = useState([]);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [userSearch, setUserSearch] = useState("");
  const [userPage, setUserPage] = useState(1);
  const [paymentPage, setPaymentPage] = useState(1);
  const [companySort, setCompanySort] = useState({ key: "createdDate", dir: "desc" });
  const [userSort, setUserSort] = useState({ key: "name", dir: "asc" });
  const [paymentSort, setPaymentSort] = useState({ key: "paymentDate", dir: "desc" });

  const sortedCompanies = useMemo(
    () => sortRows(companies, companySort),
    [companies, companySort]
  );
  const sortedUsers = useMemo(() => sortRows(users, userSort), [users, userSort]);
  const sortedPayments = useMemo(() => {
    // Prefer paymentDate; fall back to createdDate when paymentDate is empty.
    if (!paymentSort?.key || paymentSort.key !== "paymentDate") {
      return sortRows(payments, paymentSort);
    }
    const dir = paymentSort.dir === "asc" ? 1 : -1;
    return [...payments].sort((left, right) => {
      const a = left.paymentDate || left.createdDate;
      const b = right.paymentDate || right.createdDate;
      return compareSortValues(a, b) * dir;
    });
  }, [payments, paymentSort]);

  const allowed = isSuperAdminRole();

  useEffect(() => {
    if (!allowed) return;
    clearSelectedComp();
    if (officeActs.removeOfficeSelected) {
      officeActs.removeOfficeSelected();
    }
  }, [allowed, officeActs]);

  const loadCore = useCallback(async () => {
    if (!isSuperAdminRole()) return;
    setLoading(true);
    try {
      const [dashRes, companiesRes] = await Promise.all([
        getSuperAdminDashboard(),
        getSuperAdminCompanies(),
      ]);
      if (dashRes?.success) setStats(normalizeDashboard(dashRes.data));
      else Common.showErrorMessage(dashRes?.message || "Failed to load dashboard");

      if (companiesRes?.success) setCompanies((companiesRes.data || []).map(normalizeCompany));
      else Common.showErrorMessage(companiesRes?.message || "Failed to load companies");
    } catch (err) {
      console.error(err);
      Common.showErrorMessage("Something went wrong loading Super Admin data");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    if (!isSuperAdminRole()) return;
    try {
      const res = await getSuperAdminUsers({ search: userSearch, page: userPage, pageSize: 50 });
      if (res?.success) {
        setUsers((res.data || []).map(normalizeUser));
        setUsersTotal(res.total || 0);
      } else {
        Common.showErrorMessage(res?.message || "Failed to load users");
      }
    } catch (err) {
      console.error(err);
      Common.showErrorMessage("Failed to load users");
    }
  }, [userSearch, userPage]);

  const loadPayments = useCallback(async () => {
    if (!isSuperAdminRole()) return;
    try {
      const res = await getSuperAdminPayments({ page: paymentPage, pageSize: 50 });
      if (res?.success) {
        setPayments((res.data || []).map(normalizePayment));
        setPaymentsTotal(res.total || 0);
      } else {
        Common.showErrorMessage(res?.message || "Failed to load payments");
      }
    } catch (err) {
      console.error(err);
      Common.showErrorMessage("Failed to load payments");
    }
  }, [paymentPage]);

  useEffect(() => {
    if (!allowed) return;
    loadCore();
  }, [allowed, loadCore]);

  useEffect(() => {
    if (!allowed) return;
    if (activeTab === "users") loadUsers();
  }, [allowed, activeTab, loadUsers]);

  useEffect(() => {
    if (!allowed) return;
    if (activeTab === "payments") loadPayments();
  }, [allowed, activeTab, loadPayments]);

  if (!allowed) {
    return <Redirect to="/dashboard" />;
  }

  const handleEnterCompany = (company) => {
    if (companyActs.setSelectedCompany) {
      companyActs.setSelectedCompany({ id: company.id, name: company.name });
    }
    enterSuperAdminCompany(company, history);
  };

  const subscriptionBadge = (status) => {
    const s = (status || "None").toLowerCase();
    let variant = "secondary";
    if (s === "active") variant = "success";
    else if (s === "trial") variant = "info";
    else if (s === "suspended" || s === "cancelled" || s === "expired") variant = "danger";
    return <Badge bg={variant}>{status || "None"}</Badge>;
  };

  return (
    <div className="px-3 px-md-4 py-4 super-admin-dashboard">
      <CommonPageHeader
        icon={<i className="bi bi-shield-lock" />}
        title={t("app.superAdmin.title", { defaultValue: "Super Admin" })}
        description={t("app.superAdmin.description", {
          defaultValue: "Platform overview — companies, users, payments, tickets, and last login activity.",
        })}
        rightContent={
          <Link className="btn btn-primary" to="/create-company">
            <i className="bi bi-plus-lg me-1" />
            {t("app.companyDashboard.addCompany", { defaultValue: "Add Company" })}
          </Link>
        }
      />

      {loading && !stats ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" />
        </div>
      ) : (
        <>
          <div className="sa-stats-grid mb-4">
            <StatCard
              icon="bi-building"
              label={t("app.superAdmin.totalCompanies", { defaultValue: "Companies" })}
              value={stats?.totalCompanies}
              hint={`${stats?.companiesRegisteredLast30Days ?? 0} new (30d)`}
            />
            <StatCard
              icon="bi-people"
              label={t("app.superAdmin.totalUsers", { defaultValue: "Users" })}
              value={stats?.totalUsers}
              hint={`${stats?.activeUsers ?? 0} active · ${stats?.usersRegisteredLast30Days ?? 0} new (30d)`}
            />
            <StatCard
              icon="bi-box-arrow-in-right"
              label={t("app.superAdmin.logins", { defaultValue: "Logins (30d)" })}
              value={stats?.loginsLast30Days}
            />
            <StatCard
              icon="bi-credit-card"
              label={t("app.superAdmin.subscriptions", { defaultValue: "Subscriptions" })}
              value={stats?.activeSubscriptions}
              hint={`${stats?.trialSubscriptions ?? 0} trial · ${stats?.suspendedSubscriptions ?? 0} inactive`}
            />
          </div>

          <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || "companies")}>
            <Nav variant="tabs" className="mb-3">
              <Nav.Item>
                <Nav.Link eventKey="companies">
                  <i className="bi bi-building me-1" />
                  Companies
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="users">
                  <i className="bi bi-people me-1" />
                  Users
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="payments">
                  <i className="bi bi-receipt me-1" />
                  Payments
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="tickets">
                  <i className="bi bi-life-preserver me-1" />
                  Tickets
                </Nav.Link>
              </Nav.Item>
            </Nav>

            <Tab.Content>
              <Tab.Pane eventKey="companies">
                <div className="table-responsive sa-table-wrap">
                  <Table hover className="align-middle mb-0">
                    <thead>
                      <tr>
                        <SortableTh
                          label="Company"
                          sortKey="name"
                          sort={companySort}
                          onSort={(key) => setCompanySort((prev) => toggleSort(prev, key, "asc"))}
                        />
                        <SortableTh
                          label="Users"
                          sortKey="userCount"
                          sort={companySort}
                          onSort={(key) => setCompanySort((prev) => toggleSort(prev, key, "desc"))}
                        />
                        <SortableTh
                          label="Subscription"
                          sortKey="subscriptionStatus"
                          sort={companySort}
                          onSort={(key) => setCompanySort((prev) => toggleSort(prev, key, "asc"))}
                        />
                        <SortableTh
                          label="Last login"
                          sortKey="lastLoginUtc"
                          sort={companySort}
                          onSort={(key) => setCompanySort((prev) => toggleSort(prev, key, "desc"))}
                        />
                        <SortableTh
                          label="Registered"
                          sortKey="createdDate"
                          sort={companySort}
                          onSort={(key) => setCompanySort((prev) => toggleSort(prev, key, "desc"))}
                        />
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedCompanies.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-muted text-center py-4">
                            No companies yet.{" "}
                            <Link to="/create-company">Create one</Link>
                          </td>
                        </tr>
                      ) : (
                        sortedCompanies.map((c) => (
                          <tr key={c.id}>
                            <td>
                              <div className="fw-semibold">{c.name}</div>
                              <div className="text-muted small">{c.companyEmail || "—"}</div>
                            </td>
                            <td>
                              {c.userCount}
                              <span className="text-muted small"> ({c.activeUserCount} active)</span>
                            </td>
                            <td>{subscriptionBadge(c.subscriptionStatus)}</td>
                            <td>{formatDate(c.lastLoginUtc, { withTime: true })}</td>
                            <td>{formatDate(c.createdDate)}</td>
                            <td className="text-end">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => handleEnterCompany(c)}
                              >
                                Enter
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </Tab.Pane>

              <Tab.Pane eventKey="users">
                <Form
                  className="d-flex gap-2 mb-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    setUserPage(1);
                    loadUsers();
                  }}
                >
                  <Form.Control
                    type="search"
                    placeholder="Search name or email…"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                  />
                  <Button type="submit" variant="primary">
                    Search
                  </Button>
                </Form>
                <div className="table-responsive sa-table-wrap">
                  <Table hover className="align-middle mb-0">
                    <thead>
                      <tr>
                        <SortableTh
                          label="Name"
                          sortKey="name"
                          sort={userSort}
                          onSort={(key) => setUserSort((prev) => toggleSort(prev, key, "asc"))}
                        />
                        <SortableTh
                          label="Email"
                          sortKey="email"
                          sort={userSort}
                          onSort={(key) => setUserSort((prev) => toggleSort(prev, key, "asc"))}
                        />
                        <SortableTh
                          label="Company"
                          sortKey="companyName"
                          sort={userSort}
                          onSort={(key) => setUserSort((prev) => toggleSort(prev, key, "asc"))}
                        />
                        <SortableTh
                          label="Role"
                          sortKey="mainRole"
                          sort={userSort}
                          onSort={(key) => setUserSort((prev) => toggleSort(prev, key, "asc"))}
                        />
                        <SortableTh
                          label="Status"
                          sortKey="isAccountActive"
                          sort={userSort}
                          onSort={(key) => setUserSort((prev) => toggleSort(prev, key, "desc"))}
                        />
                        <SortableTh
                          label="Last login"
                          sortKey="lastLoginUtc"
                          sort={userSort}
                          onSort={(key) => setUserSort((prev) => toggleSort(prev, key, "desc"))}
                        />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedUsers.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-muted text-center py-4">
                            No users found
                          </td>
                        </tr>
                      ) : (
                        sortedUsers.map((u) => (
                          <tr key={u.id}>
                            <td>{u.name || "—"}</td>
                            <td>{u.email}</td>
                            <td>{u.companyName || "—"}</td>
                            <td>{u.mainRole || "—"}</td>
                            <td>
                              <Badge bg={u.isAccountActive ? "success" : "secondary"}>
                                {u.isAccountActive ? "Active" : "Inactive"}
                              </Badge>
                            </td>
                            <td>{formatDate(u.lastLoginUtc, { withTime: true })}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <span className="text-muted small">{usersTotal} users</span>
                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={userPage <= 1}
                      onClick={() => setUserPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={userPage * 50 >= usersTotal}
                      onClick={() => setUserPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </Tab.Pane>

              <Tab.Pane eventKey="payments">
                <div className="table-responsive sa-table-wrap">
                  <Table hover className="align-middle mb-0">
                    <thead>
                      <tr>
                        <SortableTh
                          label="Company"
                          sortKey="companyName"
                          sort={paymentSort}
                          onSort={(key) => setPaymentSort((prev) => toggleSort(prev, key, "asc"))}
                        />
                        <SortableTh
                          label="Amount"
                          sortKey="amount"
                          sort={paymentSort}
                          onSort={(key) => setPaymentSort((prev) => toggleSort(prev, key, "desc"))}
                        />
                        <SortableTh
                          label="Status"
                          sortKey="paymentStatus"
                          sort={paymentSort}
                          onSort={(key) => setPaymentSort((prev) => toggleSort(prev, key, "asc"))}
                        />
                        <SortableTh
                          label="Type"
                          sortKey="paymentType"
                          sort={paymentSort}
                          onSort={(key) => setPaymentSort((prev) => toggleSort(prev, key, "asc"))}
                        />
                        <SortableTh
                          label="Users"
                          sortKey="userCount"
                          sort={paymentSort}
                          onSort={(key) => setPaymentSort((prev) => toggleSort(prev, key, "desc"))}
                        />
                        <SortableTh
                          label="Payment date"
                          sortKey="paymentDate"
                          sort={paymentSort}
                          onSort={(key) => setPaymentSort((prev) => toggleSort(prev, key, "desc"))}
                        />
                        <SortableTh
                          label="Reference"
                          sortKey="paymentReference"
                          sort={paymentSort}
                          onSort={(key) => setPaymentSort((prev) => toggleSort(prev, key, "asc"))}
                        />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedPayments.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-muted text-center py-4">
                            No payment history
                          </td>
                        </tr>
                      ) : (
                        sortedPayments.map((p) => (
                          <tr key={p.id}>
                            <td>{p.companyName || p.companyId}</td>
                            <td>{formatMoney(p.amount, p.currency)}</td>
                            <td>{p.paymentStatus}</td>
                            <td>{p.paymentType}</td>
                            <td>{p.userCount}</td>
                            <td>{formatDate(p.paymentDate || p.createdDate)}</td>
                            <td className="small text-muted">{p.paymentReference || "—"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <span className="text-muted small">{paymentsTotal} payments</span>
                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={paymentPage <= 1}
                      onClick={() => setPaymentPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={paymentPage * 50 >= paymentsTotal}
                      onClick={() => setPaymentPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </Tab.Pane>

              <Tab.Pane eventKey="tickets">
                {activeTab === "tickets" ? <SuperAdminTicketsPanel /> : null}
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </>
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  companyActions: bindActionCreators(companyActions, dispatch),
  officeActions: bindActionCreators(officeActions, dispatch),
});

export default connect(null, mapDispatchToProps)(SuperAdminDashboard);
